/**
 * Overdue Labs Alert Modal Component
 * Shows alert when there are overdue laboratory results
 */

import { Modal, Button, Typography, Alert } from 'antd';
import { ExclamationCircleOutlined, ExperimentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { closeLabsModal } from '../../../store/dashboardSlice';

const { Title, Text, Paragraph } = Typography;

function OverdueLabsModal() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { info, activeModal } = useAppSelector(state => state.dashboard);

  const overdueLabsData = info?.overdue_labs;
  const isVisible = activeModal === 'labs' && overdueLabsData && overdueLabsData.lab_count > 0;

  if (!overdueLabsData || overdueLabsData.lab_count === 0) {
    return null;
  }

  const handleOk = () => {
    dispatch(closeLabsModal());
    // Keep the session flag set so modal doesn't show again this session
  };

  const handleViewLabs = () => {
    // Was '/encounters/main#/encounter/sendout/list' -- the AngularJS encounter module,
    // retired in 1e7c694, so this 404'd. EncountersPage carries the same list as its
    // "Sent Out" tab (badged with send_out_overdue) and reads the tab from ?tab=.
    // Client-side navigation, so the modal no longer forces a full page load.
    dispatch(closeLabsModal());
    navigate('/encounters?tab=sentout');
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span style={{ color: '#faad14' }}>Overdue Laboratory Results</span>
        </div>
      }
      open={isVisible}
      onOk={handleOk}
      onCancel={handleOk}
      width={500}
      maskClosable={true}
      footer={[
        <Button key="dismiss" onClick={handleOk}>
          Dismiss
        </Button>,
        <Button key="view" type="primary" onClick={handleViewLabs}>
          <ExperimentOutlined /> View Overdue Labs
        </Button>
      ]}
    >
      <div style={{ padding: '10px 0' }}>
        <Alert
          message={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <ExperimentOutlined style={{ fontSize: '24px', color: '#faad14' }} />
              <div>
                <Title level={4} style={{ margin: 0, color: '#faad14' }}>
                  {overdueLabsData.lab_count} Overdue Lab{overdueLabsData.lab_count !== 1 ? 's' : ''}
                </Title>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Laboratory results require immediate attention
                </Text>
              </div>
            </div>
          }
          type="warning"
          showIcon={false}
          style={{
            marginBottom: '16px',
            border: '2px solid #faad14',
            backgroundColor: '#fffbe6'
          }}
        />

        {overdueLabsData.message && (
          <div style={{ marginBottom: '16px' }}>
            <Paragraph style={{ marginBottom: '8px' }}>
              <strong>Alert Details:</strong>
            </Paragraph>
            <div 
              style={{
                padding: '12px',
                backgroundColor: '#f6f8fa',
                borderRadius: '4px',
                border: '1px solid #e1e4e8',
                fontFamily: 'monospace',
                fontSize: '13px',
                whiteSpace: 'pre-wrap'
              }}
            >
              {overdueLabsData.message}
            </div>
          </div>
        )}

      </div>
    </Modal>
  );
}

export default OverdueLabsModal;